import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useEffect, useState } from 'react';
import { pageTitle, tabRail, cardShell, formatEgp, formatInt } from '../app/glass/styles';
import Hoverable from '../app/glass/Hoverable';
import Transactions from './Transactions';
import { getCashCycles, getDashboard } from '../app/store/actions/dataActions';
import moment from 'moment';

const TABS = [
  ['wallet', 'finance.wallet'],
  ['cycles', 'finance.cashCycles'],
  ['analytics', 'finance.analytics'],
];

const tipBox = {
  position: 'absolute',
  zIndex: 5,
  pointerEvents: 'none',
  padding: '8px 12px',
  borderRadius: 12,
  background: 'var(--side-bg)',
  border: '1px solid var(--s12)',
  boxShadow: '0 12px 28px var(--sh45)',
  fontSize: 12.5,
  color: 'var(--tx)',
  whiteSpace: 'nowrap',
};

function FinancePage({ getCashCycles, getDashboard, cashCycles, dashboard, isLoading }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chartTip, setChartTip] = useState(null);
  const tabParam = searchParams.get('tab');
  const tab = TABS.some(([k]) => k === tabParam) ? tabParam : 'wallet';
  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'wallet') next.delete('tab');
    else next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (tab !== 'wallet') {
      getCashCycles({});
      getDashboard({
        startDate: moment().startOf('year').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
      });
    }
  }, [tab]);

  const cycles = Array.isArray(cashCycles) ? cashCycles : cashCycles?.docs || cashCycles?.cashCycles || [];
  const orders = dashboard?.orders || {};
  const mix = orders.periodMix || {};
  const hasMix = Boolean(orders.periodMix);
  const cod = dashboard?.cod || {};
  const successful = Number(hasMix ? mix.delivered : orders.totalDelivered) || 0;
  const rto = hasMix
    ? (Number(mix.headingToYou) || 0) + (Number(mix.returnedToChainz) || 0)
    : (Number(orders.totalReturned) || 0);
  const unsuccessful = hasMix
    ? rto + (Number(mix.cancelled) || 0)
    : (Number(orders.totalCancelled) || 0) + rto;
  const deliveredForRate = successful;
  const returnedForRate = rto;
  const rateDenom = deliveredForRate + returnedForRate;
  const totalOrders = Number(orders.totalOrders) || successful + unsuccessful;
  const rate = (rateDenom > 0 ? (deliveredForRate / rateDenom) * 100 : 100).toFixed(1);
  const collectedSum = cycles.reduce((sum, c) => sum + (Number(c.collected) || 0), 0);
  const feesSum = cycles.reduce((sum, c) => sum + (Number(c.fees) || 0), 0);
  const netSum = cycles.reduce((sum, c) => sum + (Number(c.net) || 0), 0);

  const chartCycles = [...cycles].slice(0, 12).reverse();
  const maxChart = Math.max(1, ...chartCycles.map((c) => Math.max(Number(c.collected) || 0, Number(c.fees) || 0)));
  const chartW = 560;
  const chartH = 220;
  const padL = 52;
  const padR = 16;
  const padT = 20;
  const padB = 30;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const toPt = (value, i, n) => {
    const x = padL + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const y = padT + innerH - ((Number(value) || 0) / maxChart) * innerH;
    return { x, y };
  };
  const collectedPts = chartCycles.map((c, i) => toPt(c.collected, i, chartCycles.length));
  const feePts = chartCycles.map((c, i) => toPt(c.fees, i, chartCycles.length));
  const toPath = (pts) => (pts.length ? pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ') : '');
  const toArea = (pts) => {
    if (!pts.length) return '';
    const base = padT + innerH;
    return `${toPath(pts)} L${pts[pts.length - 1].x} ${base} L${pts[0].x} ${base} Z`;
  };

  const donutR = 56;
  const donutCirc = 2 * Math.PI * donutR;
  const rateTotal = Math.max(1, rateDenom);
  const successLen = (deliveredForRate / rateTotal) * donutCirc;
  const failLen = (returnedForRate / rateTotal) * donutCirc;

  return (
    <>
      <div className="cz-wrap" style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 6 }}>
        <div style={pageTitle}>{t('navigation.finance')}</div>
        <div className="cz-scroll-x" style={{ ...tabRail, marginInlineStart: 'auto' }}>
          {TABS.map(([key, label]) => (
            <button key={key} type="button" className={`cz-tab ${tab === key ? 'on' : ''}`} onClick={() => setTab(key)}>
              {t(label)}
            </button>
          ))}
        </div>
      </div>
      {tab === 'wallet' && <Transactions />}
      {tab === 'cycles' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div
            className="cz-g1"
            style={{
              borderRadius: 26,
              padding: '24px 34px',
              background: 'var(--card-solid)',
              border: '1px solid var(--s12)',
              boxShadow: 'inset 0 1px 0 var(--s14), 0 22px 50px var(--sh45)',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 24,
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('finance.collectedCash')}</div>
              <div style={{ fontSize: 30, fontWeight: 500 }}>EGP {formatEgp(collectedSum)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('finance.cycleFees')}</div>
              <div style={{ fontSize: 30, fontWeight: 500, color: '#e2707c' }}>EGP {formatEgp(feesSum)}</div>
            </div>
            <div style={{ justifySelf: 'end', textAlign: 'end' }}>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('finance.netProfit')}</div>
              <div style={{ fontSize: 30, fontWeight: 600 }}>EGP {formatEgp(netSum)}</div>
            </div>
          </div>

          <div className="cz-g1" style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 20, alignItems: 'start' }}>
            <div
              style={{
                borderRadius: 24,
                padding: '24px 26px',
                background: 'var(--card-solid)',
                border: '1px solid var(--s10)',
                boxShadow: 'inset 0 1px 0 var(--s10), 0 18px 44px var(--sh40)',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 17, fontWeight: 600 }}>{t('finance.cashCycles')}</div>
                <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 16, fontSize: 11.5, color: 'var(--tx2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 9, height: 9, borderRadius: 3, background: '#ACBBC6' }} />
                    {t('finance.collected')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 9, height: 9, borderRadius: 3, background: '#c0303e' }} />
                    {t('finance.fees')}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--mut)', marginBottom: 14 }}>{t('finance.cyclesChartHint')}</div>
              {chartTip && (
                <div style={{ ...tipBox, top: 56, insetInlineEnd: 24 }}>
                  <div style={{ fontWeight: 600 }}>{chartTip.label}</div>
                  <div style={{ color: 'var(--tx2)', marginTop: 2 }}>{chartTip.line}: {formatEgp(chartTip.value)} EGP</div>
                </div>
              )}
              {chartCycles.length === 0 ? (
                <div style={{ color: 'var(--mut)', padding: '24px 0' }}>{t('common.noData')}</div>
              ) : (
                <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', display: 'block' }} onMouseLeave={() => setChartTip(null)}>
                  <defs>
                    <linearGradient id="finColFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(172,187,198,0.30)" />
                      <stop offset="100%" stopColor="rgba(172,187,198,0)" />
                    </linearGradient>
                    <linearGradient id="finFeeFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(192,48,62,0.35)" />
                      <stop offset="100%" stopColor="rgba(192,48,62,0)" />
                    </linearGradient>
                  </defs>
                  <g stroke="var(--s06)" strokeWidth="1">
                    <line x1={padL} y1={padT} x2={chartW - padR} y2={padT} />
                    <line x1={padL} y1={padT + innerH / 2} x2={chartW - padR} y2={padT + innerH / 2} />
                    <line x1={padL} y1={padT + innerH} x2={chartW - padR} y2={padT + innerH} />
                  </g>
                  <g fill="var(--mut)" fontSize="10">
                    <text x="8" y={padT + 4}>{formatInt(maxChart)}</text>
                    <text x="8" y={padT + innerH + 4}>0</text>
                  </g>
                  <path d={toArea(collectedPts)} fill="url(#finColFill)" />
                  <path d={toPath(collectedPts)} fill="none" stroke="#ACBBC6" strokeWidth="2.5" strokeLinecap="round" />
                  <path d={toArea(feePts)} fill="url(#finFeeFill)" />
                  <path d={toPath(feePts)} fill="none" stroke="#c0303e" strokeWidth="2.5" strokeLinecap="round" />
                  {collectedPts.map((p, i) => (
                    <circle
                      key={`c-${i}`}
                      cx={p.x}
                      cy={p.y}
                      r="10"
                      fill="transparent"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setChartTip({ label: chartCycles[i].title, line: t('finance.collected'), value: chartCycles[i].collected })}
                    />
                  ))}
                  {feePts.map((p, i) => (
                    <circle
                      key={`f-${i}`}
                      cx={p.x}
                      cy={p.y}
                      r="10"
                      fill="transparent"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setChartTip({ label: chartCycles[i].title, line: t('finance.fees'), value: chartCycles[i].fees })}
                    />
                  ))}
                  <g fill="var(--mut)" fontSize="10" textAnchor="middle">
                    {collectedPts.map((p, i) => (
                      <text key={i} x={p.x} y={chartH - 8}>{moment(chartCycles[i].createdAt).format('DD MMM')}</text>
                    ))}
                  </g>
                </svg>
              )}
            </div>

            <div
              style={{
                borderRadius: 24,
                padding: '24px 26px',
                background: 'var(--card-solid)',
                border: '1px solid var(--s10)',
                boxShadow: 'inset 0 1px 0 var(--s10), 0 18px 44px var(--sh40)',
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 600 }}>{t('finance.cashOuts')}</div>
              <div style={{ fontSize: 11.5, color: 'var(--mut)', marginTop: 3, marginBottom: 14 }}>{t('finance.cashOutsHint')}</div>
              {isLoading && <div style={{ color: 'var(--mut)' }}>{t('common.loading')}</div>}
              {!isLoading && cycles.length === 0 && <div style={{ color: 'var(--mut)' }}>{t('common.noData')}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflow: 'auto' }}>
                {cycles.slice(0, 8).map((c, i) => (
                  <Hoverable
                    key={c._id || c.title || i}
                    base={{
                      display: 'grid',
                      gridTemplateColumns: '1.4fr 1fr 1fr',
                      gap: 8,
                      padding: '10px 4px',
                      borderBottom: '1px solid var(--s08)',
                      fontSize: 13,
                    }}
                    hover={{ background: 'var(--s05)' }}
                  >
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.title || '—'}</div>
                    <div style={{ color: 'var(--mut)' }}>{moment(c.createdAt).format('DD MMM YYYY')}</div>
                    <div style={{ textAlign: 'end', fontWeight: 600 }}>{formatEgp(c.payout || c.net)} EGP</div>
                  </Hoverable>
                ))}
              </div>
            </div>
          </div>

          <div className="cz-scroll-x" style={{ ...cardShell, overflowX: 'auto' }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{t('finance.cashCycles')}</div>
            <div style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 14 }}>{t('finance.cashCyclesHint')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', gap: 12, padding: '0 0 10px', fontSize: 11, color: 'var(--mut)', minWidth: 640 }}>
              <div>{t('transactions.cashoutId')}</div>
              <div>{t('common.date')}</div>
              <div>{t('finance.collection')}</div>
              <div>{t('finance.fees')}</div>
              <div>{t('finance.balance')}</div>
            </div>
            {isLoading && <div style={{ color: 'var(--mut)' }}>{t('common.loading')}</div>}
            {!isLoading && cycles.length === 0 && <div style={{ color: 'var(--mut)', padding: '20px 0' }}>{t('common.noData')}</div>}
            {cycles.map((c, i) => (
              <div key={c._id || i} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--s08)', minWidth: 640, fontSize: 13.5 }}>
                <div style={{ fontWeight: 600 }}>{c.title || '—'}</div>
                <div style={{ color: 'var(--tx2)' }}>{moment(c.createdAt).format('DD MMM YYYY')}</div>
                <div>{formatEgp(c.collected)} EGP</div>
                <div style={{ color: '#e2707c' }}>{formatEgp(c.fees)} EGP</div>
                <div style={{ fontWeight: 600 }}>{formatEgp(c.net)} EGP</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === 'analytics' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div
            className="cz-g2"
            style={{
              borderRadius: 26,
              padding: '24px 34px',
              background: 'var(--card-solid)',
              border: '1px solid var(--s12)',
              boxShadow: 'inset 0 1px 0 var(--s14), 0 22px 50px var(--sh45)',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1.3fr',
              gap: 24,
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('finance.totalOrders')}</div>
              <div style={{ fontSize: 30, fontWeight: 500 }}>{formatInt(totalOrders)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('finance.successful')}</div>
              <div style={{ fontSize: 30, fontWeight: 500 }}>{formatInt(successful)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('finance.unsuccessful')}</div>
              <div style={{ fontSize: 30, fontWeight: 500, color: '#e2707c' }}>{formatInt(unsuccessful)}</div>
            </div>
            <div style={{ justifySelf: 'end', textAlign: 'end' }}>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{t('finance.cashCollected')}</div>
              <div style={{ fontSize: 30, fontWeight: 500 }}>EGP {formatEgp(cod.totalIncome)}</div>
            </div>
          </div>

          <div className="cz-g1" style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 20, alignItems: 'start' }}>
            <div style={cardShell}>
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 14 }}>{t('finance.deliverySuccessRate')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <svg viewBox="0 0 150 150" style={{ width: 140, flexShrink: 0, display: 'block' }}>
                  <g transform="translate(75, 75)">
                    <circle r={donutR} fill="none" stroke="var(--s08)" strokeWidth="16" />
                    <circle
                      r={donutR}
                      fill="none"
                      stroke="rgba(172,187,198,0.85)"
                      strokeWidth="16"
                      strokeDasharray={`${Math.max(successLen, 0.01)} ${donutCirc}`}
                      transform="rotate(-90)"
                      strokeLinecap="round"
                    />
                    <circle
                      r={donutR}
                      fill="none"
                      stroke="rgba(192,48,62,0.9)"
                      strokeWidth="16"
                      strokeDasharray={`${Math.max(failLen, 0.01)} ${donutCirc}`}
                      strokeDashoffset={-successLen}
                      transform="rotate(-90)"
                      strokeLinecap="round"
                    />
                    <text y="-2" textAnchor="middle" fill="var(--tx)" fontSize="22" fontWeight="600">{rate}%</text>
                    <text y="18" textAnchor="middle" fill="var(--mut)" fontSize="10">{t('finance.successful')}</text>
                  </g>
                </svg>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 3, background: '#ACBBC6' }} />
                    <span style={{ color: 'var(--tx2)' }}>{t('home.delivered')}</span>
                    <span style={{ marginInlineStart: 'auto', fontWeight: 600 }}>{formatInt(deliveredForRate)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 3, background: '#c0303e' }} />
                    <span style={{ color: 'var(--tx2)' }}>{t('home.returnedOrders')}</span>
                    <span style={{ marginInlineStart: 'auto', fontWeight: 600 }}>{formatInt(returnedForRate)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div style={cardShell}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>{t('finance.orderMix')}</div>
              {[
                [t('home.newOrders'), hasMix ? mix.new : null],
                [t('home.inProgress'), hasMix ? mix.inProgress : orders.totalFulfilled],
                [t('home.enRoute'), hasMix ? mix.headingToCustomer : orders.totalEnRoute],
                [t('home.delivered'), hasMix ? mix.delivered : orders.totalDelivered],
                [t('home.returnedOrders'), hasMix ? (Number(mix.headingToYou) || 0) + (Number(mix.returnedToChainz) || 0) : orders.totalReturned],
                [t('common.cancel'), hasMix ? mix.cancelled : orders.totalCancelled],
                [t('home.awaitingAction'), hasMix ? mix.awaitingAction : orders.totalOther],
              ].filter(([, value]) => value != null).map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--s08)', fontSize: 13 }}>
                  <span style={{ color: 'var(--tx2)' }}>{label}</span>
                  <span style={{ fontWeight: 600 }}>{value || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  cashCycles: state?.records?.cashCycles,
  dashboard: state?.records?.dashboard,
  isLoading: state?.records?.isLoading,
});

export default connect(mapStateToProps, { getCashCycles, getDashboard })(FinancePage);
